import { useCallback, useEffect, useRef, useState } from 'react';

import { explain } from '@/api';
import { useCloudSynced } from '@/hooks';
import { haptic } from '@/platform';

export const RATES = [1, 1.5, 2] as const;
export type Rate = (typeof RATES)[number];

const RATE_KEY = 'explain_audio_rate';
const TICK_MS = 250;
const DONE_MS = 2600;

export interface AudioTrack {
  index: number;
  questionId: string;
  duration: number;
  url: string | null;
}

interface AudioState {
  track: AudioTrack | null;
  playing: boolean;
  t: number;
  done: boolean;
  loading: boolean;
  error: boolean;
  hidden: boolean;
}

const IDLE: AudioState = {
  track: null,
  playing: false,
  t: 0,
  done: false,
  loading: false,
  error: false,
  hidden: false,
};

const parseRate = (raw: unknown): Rate | null => {
  const n = Number(raw);
  return RATES.find((r) => r === n) ?? null;
};

export const rateLabel = (r: Rate) => (r === 1.5 ? '1,5×' : `${r}×`);

export const clock = (s: number) =>
  `${Math.floor(s / 60)}:${String(Math.floor(s) % 60).padStart(2, '0')}`;

export interface ExplainAudio extends AudioState {
  rate: Rate;
  progress: number;
  cycleRate: () => void;
  toggle: (track: AudioTrack) => void;
  retry: () => void;
  stop: () => void;
  reset: () => void;
  pause: () => void;
}

export function useExplainAudio(): ExplainAudio {
  const [state, setState] = useState<AudioState>(IDLE);
  const [rate, setRate] = useCloudSynced<Rate>(RATE_KEY, parseRate, 1);
  const ready = useRef<Set<string>>(new Set());
  const media = useRef<HTMLAudioElement | null>(null);
  const doneTimer = useRef<number | null>(null);
  const rateRef = useRef(rate);
  const tRef = useRef(0);

  useEffect(() => {
    tRef.current = state.t;
  }, [state.t]);

  useEffect(() => {
    rateRef.current = rate;
    if (media.current) media.current.playbackRate = rate;
  }, [rate]);

  const finish = useCallback(() => {
    setState((s) => ({ ...s, t: 0, playing: false, done: true }));
    if (doneTimer.current !== null) clearTimeout(doneTimer.current);
    doneTimer.current = window.setTimeout(() => {
      doneTimer.current = null;
      setState((s) => ({ ...s, done: false }));
    }, DONE_MS);
  }, []);

  const simulated = state.playing && state.track !== null && state.track.url === null;
  const duration = state.track?.duration ?? 0;
  useEffect(() => {
    if (!simulated) return;
    const id = window.setInterval(() => {
      const next = tRef.current + rateRef.current * (TICK_MS / 1000);
      tRef.current = next;
      if (next >= duration) finish();
      else setState((s) => ({ ...s, t: next }));
    }, TICK_MS);
    return () => clearInterval(id);
  }, [simulated, duration, finish]);

  useEffect(
    () => () => {
      if (doneTimer.current !== null) clearTimeout(doneTimer.current);
      media.current?.pause();
    },
    [],
  );

  const pause = useCallback(() => {
    media.current?.pause();
    setState((s) => ({ ...s, playing: false }));
  }, []);

  const fail = useCallback(() => {
    haptic.notify('error');
    setState((s) => ({ ...s, loading: false, playing: false, error: true }));
  }, []);

  const start = useCallback(
    (track: AudioTrack) => {
      if (track.url) {
        let el = media.current;
        if (!el || el.dataset.src !== track.url) {
          el?.pause();
          el = new Audio(track.url);
          el.dataset.src = track.url;
          el.ontimeupdate = () => setState((s) => ({ ...s, t: el?.currentTime ?? s.t }));
          el.onended = finish;
          el.onerror = fail;
          media.current = el;
        }
        el.playbackRate = rateRef.current;
        void el.play().catch(fail);
      }
      setState((s) => ({ ...s, loading: false, playing: true, done: false, hidden: false }));
    },
    [fail, finish],
  );

  const toggle = useCallback(
    (track: AudioTrack) => {
      if (state.playing && state.track?.questionId === track.questionId) return pause();
      if (state.loading) return;
      const same = state.track?.questionId === track.questionId;
      setState((s) => ({
        ...(same ? s : IDLE),
        track,
        error: false,
        done: false,
        hidden: false,
        loading: !ready.current.has(track.questionId),
      }));
      if (ready.current.has(track.questionId)) return start(track);
      explain.prepareAudio(track.questionId).then(() => {
        ready.current.add(track.questionId);
        start(track);
      }, fail);
    },
    [state.playing, state.loading, state.track, pause, start, fail],
  );

  const retry = useCallback(() => {
    setState((s) => ({ ...s, error: false }));
    if (state.track) toggle(state.track);
  }, [state.track, toggle]);

  const stop = useCallback(() => {
    media.current?.pause();
    setState((s) => ({ ...s, playing: false, hidden: true }));
  }, []);

  const reset = useCallback(() => {
    media.current?.pause();
    media.current = null;
    setState(IDLE);
  }, []);

  const cycleRate = useCallback(
    () => setRate((r) => RATES[(RATES.indexOf(r) + 1) % RATES.length]),
    [setRate],
  );

  return {
    ...state,
    rate,
    progress: duration > 0 ? Math.min(1, state.t / duration) : 0,
    cycleRate,
    toggle,
    retry,
    stop,
    reset,
    pause,
  };
}
